import { useMutation, useQuery } from '@apollo/client'
import { MutationBaseOptions } from '@apollo/client/core/watchQueryOptions'
import { Autocomplete, AutocompleteProps, TextField } from '@mui/material'
import { GridRenderEditCellParams } from '@mui/x-data-grid'
import {
    AddToRosterMutation,
    GetProcessStatusOptionsQuery,
} from '__generated__/graphql'
import { FC } from 'react'
import { GetProcessStatusOptions } from 'recruiter/graphql/processStatusOptions.gql'
import { useCandidateSearch } from 'recruiter/hooks'
import useToast from 'shared/components/Toast/useToast'
import {
    formatGraphQLError,
    sortProcessStatusesBySummaryGroup,
} from 'shared/utils'
import { AddToRoster } from '../CompareTable/Compare.gql'

interface Props extends GridRenderEditCellParams {
    candidateId: string
    /** An array (or a function that returns an array) that specifies which queries you want to refetch after this cell is successfully edited.  */
    refetchQueries?: MutationBaseOptions['refetchQueries']
    rosterId: string
    onSuccess?: (
        params: GridRenderEditCellParams,
        rosterCandidate: AddToRosterMutation['addCandidatesToRoster']['data']
    ) => void
    canEdit: boolean
}

const ProcessStatusEditWithAddToRoster: FC<Props> = ({
    candidateId,
    refetchQueries,
    rosterId,
    canEdit,
    onSuccess,
    ...params
}) => {
    const toast = useToast()
    const { refetch } = useCandidateSearch()
    const { data: processStatusOptions } = useQuery(GetProcessStatusOptions)

    const [addToRoster] = useMutation(AddToRoster, {
        refetchQueries,
        onCompleted(data) {
            if (data.addCandidatesToRoster.success) {
                if (params.api.getRowsCount() === 1) {
                    /* When we remove a row, MUI grid automatically sets focus to that same index. If we have removed the final row, this behavior will throw an error unless we manually direct the focus ourselves using this call: */
                    params.api.setColumnHeaderFocus('rosterName')
                }

                toast({
                    title: 'Success',
                    message:
                        data.addCandidatesToRoster.message ||
                        'Candidate added to roster successfully',
                    severity: 'success',
                })

                if (onSuccess)
                    onSuccess(params, data.addCandidatesToRoster.data)

                if (refetch) {
                    refetch()
                }
            } else {
                toast({
                    title: 'Error',
                    message:
                        data.addCandidatesToRoster.message ||
                        'Error adding candidate to roster',
                    severity: 'error',
                })
            }
        },
        onError(error) {
            toast({
                title: 'Error',
                message: formatGraphQLError(
                    error,
                    'Error updating process status'
                ),
                severity: 'error',
            })
        },
    })

    const handleValueChange: AutocompleteProps<
        NonNullable<GetProcessStatusOptionsQuery['processStatuses'][number]>,
        false,
        false,
        false
    >['onChange'] = (event, value) => {
        addToRoster({
            variables: {
                candidateIds: [candidateId],
                rosterId,
                processStatusId: value?.id,
            },
        })
    }

    return (
        <Autocomplete
            disabled={!canEdit}
            onChange={handleValueChange}
            options={sortProcessStatusesBySummaryGroup(
                processStatusOptions?.processStatuses ?? []
            )}
            value={params.row?.status ?? null}
            getOptionLabel={(option) => option.statusLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            groupBy={(option) => option.processStatusSummaryGroup?.label || ''}
            disableClearable
            fullWidth
            size="small"
            sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                '& .MuiOutlinedInput-root': {
                    fontSize: '0.75rem',
                    py: 0,
                    '& fieldset': { borderColor: 'divider' },
                    '&:hover fieldset': { borderColor: 'text.secondary' },
                },
            }}
            renderOption={({ key, ...optionProps }, option, { selected }) => (
                <li
                    key={key}
                    {...optionProps}
                    style={{
                        fontSize: '0.75rem',
                        paddingTop: 4,
                        paddingBottom: 4,
                        minHeight: 'unset',
                        backgroundColor: selected
                            ? 'rgba(25, 118, 210, 0.08)'
                            : undefined,
                        borderLeft: selected ? '3px solid #1976d2' : 'none',
                    }}
                >
                    {option.statusLabel}
                </li>
            )}
            slotProps={{
                paper: {
                    sx: {
                        '& .MuiAutocomplete-groupLabel': {
                            fontSize: '0.6875rem',
                            lineHeight: 2,
                            fontWeight: 600,
                        },
                    },
                },
            }}
            renderInput={(renderParams) => (
                <TextField
                    {...renderParams}
                    fullWidth
                    placeholder={'Quick Add to Search'}
                />
            )}
        />
    )
}

export default ProcessStatusEditWithAddToRoster
